import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { HomeIcon, HammerIcon, ArrowRightIcon, ShieldCheckIcon, ZapIcon, TrendingUpIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 40
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  }
};
export function ServicesOverview() {
  const {
    t
  } = useLanguage();
  const services = [{
    path: '/construccion',
    icon: HomeIcon,
    title: t('services.construction.title'),
    description: t('services.construction.desc'),
    image: 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=800&q=80',
    features: [t('services.construction.f1'), t('services.construction.f2'), t('services.construction.f3'), t('services.construction.f4')]
  }, {
    path: '/reformas',
    icon: HammerIcon,
    title: t('services.renovations.title'),
    description: t('services.renovations.desc'),
    image: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800&q=80',
    features: [t('services.renovations.f1'), t('services.renovations.f2'), t('services.renovations.f3'), t('services.renovations.f4')]
  }];
  const values = [{
    icon: ShieldCheckIcon,
    label: t('values.trust'),
    desc: t('values.trust.desc')
  }, {
    icon: ZapIcon,
    label: t('values.efficiency'),
    desc: t('values.efficiency.desc')
  }, {
    icon: TrendingUpIcon,
    label: t('values.strategy'),
    desc: t('values.strategy.desc')
  }];
  return <section className="w-full bg-white py-14 sm:py-20 lg:py-24 px-4 sm:px-6" data-id="element-303">
      <div className="max-w-7xl mx-auto" data-id="element-304">
        {/* Section header */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.6
      }} className="text-center mb-10 sm:mb-14" data-id="element-305">
          <motion.span className="text-[10px] sm:text-xs font-medium tracking-widest text-neutral-500 uppercase mb-2 sm:mb-3 block" initial={{
          opacity: 0,
          y: 15
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} data-id="element-306">
            {t('services.label')}
          </motion.span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-light text-black mb-4 sm:mb-5" data-id="element-307">
            {t('services.title1')}
            <br data-id="element-308" />
            <span className="text-neutral-400 font-normal" data-id="element-309">
              {t('services.title2')}
            </span>
          </h2>
          <p className="text-sm sm:text-base text-neutral-600 max-w-2xl mx-auto leading-relaxed font-light mb-8 sm:mb-10" data-id="element-310">
            {t('services.subtitle')}
          </p>

          {/* Values */}
          <div className="grid sm:grid-cols-3 gap-4 sm:gap-6 max-w-4xl mx-auto mb-10 sm:mb-14" data-id="element-311">
            {values.map((val, idx) => <motion.div key={idx} className="text-center p-4 sm:p-5 bg-neutral-50 rounded-xl border border-neutral-100" initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1 + idx * 0.08
          }} data-id="element-312">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-black rounded-xl flex items-center justify-center mx-auto mb-3" data-id="element-313">
                  <val.icon className="w-5 h-5 sm:w-6 sm:h-6 text-white" data-id="element-314" />
                </div>
                <h3 className="font-medium text-black text-sm sm:text-base mb-1" data-id="element-315">
                  {val.label}
                </h3>
                <p className="text-neutral-600 text-xs sm:text-sm font-light leading-relaxed" data-id="element-316">
                  {val.desc}
                </p>
              </motion.div>)}
          </div>
        </motion.div>

        {/* Services grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} className="grid md:grid-cols-2 gap-4 sm:gap-6" data-id="element-317">
          {services.map(service => <motion.div key={service.path} variants={itemVariants} className="group relative bg-white rounded-2xl overflow-hidden border border-neutral-100 shadow-sm hover:shadow-lg transition-all duration-400" data-id="element-318">
              {/* Image */}
              <div className="relative h-48 sm:h-56 lg:h-64 overflow-hidden" data-id="element-319">
                <motion.img src={service.image} alt={service.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" style={{
              filter: 'saturate(0.9)'
            }} data-id="element-320" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" data-id="element-321" />
                <motion.div className="absolute bottom-4 left-4" initial={{
              opacity: 0,
              y: 15
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: 0.2
            }} data-id="element-322">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 bg-white rounded-xl flex items-center justify-center shadow-lg" data-id="element-323">
                    <service.icon className="w-6 h-6 sm:w-7 sm:h-7 text-black" data-id="element-324" />
                  </div>
                </motion.div>
              </div>

              {/* Content */}
              <div className="p-5 sm:p-6 lg:p-8" data-id="element-325">
                <h3 className="text-lg sm:text-xl lg:text-2xl font-normal text-black mb-2 sm:mb-3" data-id="element-326">
                  {service.title}
                </h3>
                <p className="text-neutral-600 mb-5 sm:mb-6 leading-relaxed text-sm sm:text-base font-light" data-id="element-327">
                  {service.description}
                </p>

                {/* Features */}
                <div className="flex flex-wrap gap-1.5 sm:gap-2 mb-5 sm:mb-6" data-id="element-328">
                  {service.features.map((feature, index) => <motion.span key={index} className="px-2.5 sm:px-3 py-1 sm:py-1.5 bg-neutral-50 border border-neutral-100 text-neutral-700 text-[10px] sm:text-xs font-normal rounded-full" initial={{
                opacity: 0,
                scale: 0.95
              }} whileInView={{
                opacity: 1,
                scale: 1
              }} viewport={{
                once: true
              }} transition={{
                delay: 0.08 * index
              }} data-id="element-329">
                      {feature}
                    </motion.span>)}
                </div>

                {/* CTA */}
                <Link to={service.path} data-id="element-330">
                  <motion.span className="inline-flex items-center gap-2 text-black font-medium text-sm sm:text-base group/btn" whileHover={{
                x: 4
              }} data-id="element-331">
                    {t('services.more')}
                    <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-black text-white flex items-center justify-center transition-colors group-hover/btn:bg-neutral-800" data-id="element-332">
                      <ArrowRightIcon className="w-3 h-3 sm:w-3.5 sm:h-3.5" data-id="element-333" />
                    </div>
                  </motion.span>
                </Link>
              </div>
            </motion.div>)}
        </motion.div>
      </div>
    </section>;
}